import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { User } from "lucide-react";
import { auth, db, storage } from "../firebase/firebaseConfig"; // ✅ add storage
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"; // ✅ storage utils
import { UserContext } from "../context/UserContext";
import { toast } from "react-toastify";

// styled-components (same as before)
const Container = styled.div`
  background: #121212;
  min-height: 100vh;
  padding: 20px;
  color: #f1f1f1;
  font-family: Arial, sans-serif;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;
const Card = styled.div`
  background: #1e1e1e;
  padding: 30px;
  width: 100%;
  max-width: 600px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
`;
const Title = styled.h2`
  margin-bottom: 20px;
  font-size: 22px;
  color: #fff;
  text-align: center;
`;
const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 15px;
  border-radius: 8px;
  border: none;
  background: #2c2c2c;
  color: #fff;
  font-size: 15px;
`;
const Dropdown = styled.select`
  width: 100%;
  padding: 12px;
  margin-bottom: 15px;
  border-radius: 8px;
  border: none;
  background: #2c2c2c;
  color: #fff;
  font-size: 15px;
`;
const Button = styled.button`
  background: #6200ea;
  color: white;
  padding: 12px 18px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  width: 100%;
  margin-top: 20px;
  &:hover {
    background: #4500b5;
  }
`;
const FileUploadWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;
const FileLabel = styled.label`
  border: 1px solid #fff;
  border-radius: 8px;
  padding: 6px 14px;
  cursor: pointer;
  color: #fff;
  background: transparent;
  transition: all 0.2s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;
const FileInput = styled.input`
  display: none;
`;
const ProfilePicWrapper = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #fff;
  margin: 0 auto 10px auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
`;
const ProfileImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;
const Loader = styled.div`
  border: 6px solid #f3f3f3;
  border-top: 6px solid #a855f7;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export default function PortfolioForm() {
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    address: "",
    company: "",
    profile: "",
  });
  const [profilePic, setProfilePic] = useState(null);
  const [file, setFile] = useState(null); // ✅ store selected file
  const { user, setUser } = useContext(UserContext);
  const [loading, setLoading] = useState(false);

  // Fetch user data
  useEffect(() => {
    getUser();
  }, []);

  const getUser = async () => {
    if (user?.id) {
      setLoading(true);
      const userDocRef = doc(db, "users", user.id);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        setForm({
          name: data.name || "",
          mobile: data.mobile || "",
          address: data.address || "",
          company: data.company || "",
          profile: data.profile || "",
        });
        setProfilePic(data.profilePic || null);
        setUser(data);
      }
      setLoading(false);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle profile pic file selection
  const handlePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file); // save file for upload
      setProfilePic(URL.createObjectURL(file)); // preview
    }
  };

  // Upload file to Firebase Storage & return URL
  const uploadProfilePic = async (file) => {
    if (!file || !user?.id) return null;
    const fileRef = ref(storage, `users/${user.id}/profile.jpg`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  // Save user data in Firestore
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setLoading(true);

    let profilePicUrl = profilePic;
    if (file) {
      profilePicUrl = await uploadProfilePic(file);
    }

    const userRef = doc(db, "users", user.id);
    await setDoc(
      userRef,
      {
        ...form,
        profilePic: profilePicUrl || null,
      },
      { merge: true }
    );

    setLoading(false);
    toast.success("Profile updated successfully!");
    getUser();
  };

  return (
    <Container>
      {loading && (
        <LoadingOverlay>
          <Loader />
        </LoadingOverlay>
      )}
      <Card>
        <Title>Personal Information</Title>

        {/* Profile Pic */}
        <ProfilePicWrapper>
          {profilePic ? (
            <ProfileImg src={profilePic} alt="Profile" />
          ) : (
            <User size={48} color="black" />
          )}
        </ProfilePicWrapper>

        {/* Upload Button */}
        <FileUploadWrapper>
          <FileLabel htmlFor="profilePicInput">Choose Profile Pic</FileLabel>
          <FileInput
            type="file"
            id="profilePicInput"
            accept="image/*"
            onChange={handlePicChange}
          />
        </FileUploadWrapper>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Input
            name="name"
            placeholder="Full Name"
            value={form.name}
            onChange={handleChange}
          />
          <Input
            name="mobile"
            placeholder="Mobile Number"
            value={form.mobile}
            onChange={handleChange}
          />
          <Input
            name="address"
            placeholder="Address"
            value={form.address}
            onChange={handleChange}
          />
          <Input
            name="company"
            placeholder="Current Company"
            value={form.company}
            onChange={handleChange}
          />

          {/* Dropdown */}
          <Dropdown name="profile" value={form.profile} onChange={handleChange}>
            <option value="">Select Profile</option>
            {[
              "Frontend Developer",
              "Backend Developer",
              "Full Stack Developer",
              "Mobile Developer",
              "iOS Developer",
              "Android Developer",
              "React Native Developer",
              "Flutter Developer",
              "DevOps Engineer",
              "UI/UX Designer",
              "Product Designer",
              "Web Designer",
              "Cloud Engineer",
              "AI Engineer",
              "ML Engineer",
              "Data Scientist",
              "Data Engineer",
              "Big Data Engineer",
              "Blockchain Developer",
              "Solidity Developer",
              "Smart Contract Engineer",
              "Game Developer",
              "Unity Developer",
              "Unreal Engine Developer",
              "AR/VR Developer",
              "Embedded Systems Developer",
              "IoT Developer",
              "System Architect",
              "Site Reliability Engineer",
              "Security Engineer",
              "Penetration Tester",
              "Cybersecurity Analyst",
              "Database Administrator",
              "PostgreSQL Developer",
              "MySQL Developer",
              "MongoDB Developer",
              "Oracle Developer",
              "Python Developer",
              "Java Developer",
              "C++ Developer",
              "C# Developer",
              "PHP Developer",
              "Ruby Developer",
              "Go Developer",
              "Rust Developer",
              "Kotlin Developer",
              "Swift Developer",
              "Scala Developer",
              "R Developer",
              "Perl Developer",
              "Shell Scripting Engineer",
              "Automation Engineer",
              "QA Engineer",
              "Test Automation Engineer",
              "Software Engineer",
              "Solutions Architect",
            ].map((role, i) => (
              <option key={i}>{role}</option>
            ))}
          </Dropdown>

          <Button type="submit">Save</Button>
        </form>
      </Card>
    </Container>
  );
}
